import { Box, Button, ButtonGroup, Card, Divider, HorizontalGrid, HorizontalStack, Text } from '@shopify/polaris';
import DateRangePicker from '../Common/DateRangePicker';
import DropdownFilter from '../Common/DropdownFilter';
import { VerticalSpace } from './VerticalSpace';
import FilterSelect from '../Products/FilterSelect';
import { useEffect, useState } from 'react';
import { getSearchParams, handleSearchParams } from '../../../helpers/handleSearchParams';
import { hydrateFilters } from '../../../services/productService';
import { getDeposits } from '../../../services/depositsService';
import { getCustomersList } from '../../../services/customersService';
import DateSinglePicker from '../Common/DateSinglePicker';
import { useSearchParams } from '../../../customHooks/useSearchParams';
import { useTimezone } from '../../../customHooks/useTimezone';
import { parseFilterParam } from '../../../helpers/dates';

const defaultFilters = {
  genders: {
    name: 'Género',
    slug: 'genders',
    bundle: true,
  },
  categories: {
    name: 'Categorías',
    slug: 'categories',
    bundle: true,
  },
  subcategories: {
    name: 'Subcategorías',
    slug: 'subcategories',
    bundle: true,
  },
  seasons: {
    name: 'Temporadas',
    slug: 'seasons',
    bundle: true,
  },
  years: {
    name: 'Años',
    slug: 'years',
    bundle: true,
  },
  deposits: {
    name: 'Depósitos',
    slug: 'deposits',
  },
  customers: {
    name: 'Clientes',
    slug: 'customers',
  },
};

const setLabelValue = (inputArray) => inputArray.map((i) => ({ label: i.name, value: i.id }));

export const FilterDatesCategories = ({ handleFiltersChange, isReady }) => {
  const { timezone } = useTimezone();
  const [filters, setFilters] = useState(defaultFilters);
  const [defaultDates, setDefaultDates] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    isReady(ready);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  useEffect(() => {
    if (!ready) return;
    Object.keys(filters).forEach((k) => handleSearchParams(k, filters[k].value));
    if (!filters.until || !filters.since) return;
    handleFiltersChange(filters);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    const period = {
      since: parseFilterParam(getSearchParams('since'), timezone),
      until: parseFilterParam(getSearchParams('until'), timezone),
    };

    if (period.since && period.until) setDefaultDates(period);

    Promise.all([
      hydrateFilters(
        Object.keys(defaultFilters)
          .map((k) => defaultFilters[k])
          .reduce((acc, f) => ({ ...acc, [f.slug]: f }), {}),
      ),
      getDeposits({ limit: 1000, excludeGroups: true }),
      getCustomersList({ limit: 1000 }),
    ]).then(([f, d, c]) => {
      setFilters({
        ...filters,
        ...f,
        deposits: {
          ...f.deposits,
          options: setLabelValue([...d.data.data]),
        },
        customers: {
          ...f.customers,
          options: setLabelValue([...c.data.data]),
        },
      });

      setReady(true);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearFilters = () => {
    const currentFilters = { ...filters };
    Object.keys(defaultFilters).forEach((k) => {
      currentFilters[k].value = null;
    });
    setFilters(currentFilters);
  };
  const handleFilter = ({ slug, value }) => setFilters({ ...filters, [slug]: { ...filters[slug], value } });
  const handleDateChange = (dates) => {
    Object.keys(dates).forEach((k) => handleSearchParams(k, dates[k]));

    setFilters({
      ...filters,
      since: {
        value: dates.since,
      },
      until: {
        value: dates.until,
      },
    });
  };

  return (
    <>
      {ready && (
        <Card roundedAbove="sm">
          <HorizontalGrid columns={{ xs: 1, md: '2fr 1fr 1fr 3fr' }} gap="4">
            <Box>
              <Text variant="headingMd" as="h4">
                Fechas
              </Text>
              <Box border="divider" borderRadius="base" minHeight=".5rem" />
              <DateRangePicker onDateChanges={handleDateChange} defaultDates={defaultDates} />
            </Box>
            <DFilter handleFilter={handleFilter} payload={filters.deposits} slug="deposits" label="Depósitos" />
            <DFilter handleFilter={handleFilter} payload={filters.customers} slug="customers" label="Clientes" />
            <HorizontalStack align="end">
              <Box>
                <Text variant="headingMd" as="h4">
                  Categorización
                </Text>
                <Box border="divider" borderRadius="base" minHeight=".5rem" />
                <ButtonGroup segmented>
                  {Object.keys(filters)
                    .map((k) => filters[k])
                    .filter((f) => f.bundle)
                    .map(
                      (filter) =>
                        filter && (
                          <FilterSelect
                            {...filter}
                            options={filter.options ?? []}
                            key={filter.slug}
                            defaultValue={filter.value ?? ''}
                            onFilterChange={handleFilter}
                          />
                        ),
                    )}
                </ButtonGroup>
              </Box>
            </HorizontalStack>
          </HorizontalGrid>
          <VerticalSpace />
          <Divider />
          <VerticalSpace />
          <HorizontalStack align="end">
            <Button onClick={clearFilters} plain>
              Borrar filtros
            </Button>
          </HorizontalStack>
        </Card>
      )}
    </>
  );
};

export const FilterSingleDateCategories = ({ isReady }) => {
  const { timezone } = useTimezone();
  const [ready, setReady] = useState(false);
  const [filters, setFilters] = useState(defaultFilters);
  const { addParams, removeParam } = useSearchParams();

  useEffect(() => {
    isReady && isReady(ready);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  useEffect(() => {
    Promise.all([
      hydrateFilters(
        Object.keys(defaultFilters)
          .map((k) => defaultFilters[k])
          .reduce((acc, f) => ({ ...acc, [f.slug]: f }), {}),
      ),
      getDeposits({ limit: 1000, excludeGroups: true }),
    ]).then(([f, d, c]) => {
      setFilters({
        ...filters,
        ...f,
        deposits: {
          ...f.deposits,
          options: setLabelValue([...d.data.data]),
        },
      });

      setReady(true);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // const clearFilters = () => {
  //   const currentFilters = { ...filters };
  //   Object.keys(defaultFilters).forEach((k) => {
  //     currentFilters[k].value = null;
  //   });
  //   setFilters({ ...currentFilters });
  //   console.log(currentFilters, 'clear', searchParams);
  //   clearParams();
  // };

  const handleFilter = ({ slug, value }) => (value ? addParams({ [slug]: value }) : removeParam(slug));
  // DateSinglePicker ya emite un ISO con offset construido en la zona
  // seleccionada — lo persistimos en la URL tal cual.
  const handleDateChange = (dateIso) => addParams({ date: dateIso });

  return (
    <>
      {ready && (
        <Card roundedAbove="sm">
          <HorizontalGrid columns={{ xs: 1, md: '2fr 2fr 3fr' }} gap="4">
            <Box>
              <Text variant="headingMd" as="h4">
                Fecha
              </Text>
              <Box border="divider" borderRadius="base" minHeight=".5rem" />
              <DateSinglePicker
                onDateChanges={handleDateChange}
                defaultDate={parseFilterParam(getSearchParams('date'), timezone) ?? new Date()}
              />
            </Box>
            <DFilter handleFilter={handleFilter} payload={filters.deposits} slug="deposits" label="Depósitos" />
            <HorizontalStack align="end">
              <Box>
                <Text variant="headingMd" as="h4">
                  Categorización
                </Text>
                <Box border="divider" borderRadius="base" minHeight=".5rem" />
                <ButtonGroup segmented>
                  {Object.keys(filters)
                    .map((k) => filters[k])
                    .filter((f) => f.bundle)
                    .map(
                      (filter) =>
                        filter && (
                          <FilterSelect
                            {...filter}
                            options={filter.options ?? []}
                            key={filter.slug}
                            defaultValue={filter.value ?? ''}
                            onFilterChange={handleFilter}
                          />
                        ),
                    )}
                </ButtonGroup>
              </Box>
            </HorizontalStack>
          </HorizontalGrid>
          <VerticalSpace />
          {/* <Divider />
          <VerticalSpace />
          <HorizontalStack align="end">
            <Button onClick={clearFilters} plain>
              Borrar filtros
            </Button>
          </HorizontalStack> */}
        </Card>
      )}
    </>
  );
};

const DFilter = ({ handleFilter, payload, slug, label }) => (
  <Box>
    <Text variant="headingMd" as="h4">
      {label}
    </Text>
    <Box border="divider" borderRadius="base" minHeight=".5rem" />
    <DropdownFilter
      slug={slug}
      title={label}
      onFilterChange={handleFilter}
      defaultValue={payload?.value ?? ''}
      options={payload?.options ?? []}
    />
  </Box>
);
